import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { SetReturnUrl } from './authentication/store/authentication.actions';
import { environment } from '../environments/environment';
import { currentUserData } from './authentication/store/authentication.reducer';
import { ZoomIn, ZoomOut, ZoomReset } from './ui/store/ui.actions';
import { MatomoService } from './core/services/matomo.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<void> = new Subject<void>();
  title = 'client';
  wasInitialized = false;
  isTrackingEnabled = false;
  old_paq;
  isProjectTablePage = false;

  constructor(
    private store: Store,
    private location: Location,
    private router: Router,
    private matomoService: MatomoService,
    @Inject(DOCUMENT) private document
  ) {}

  @HostListener('window:beforeunload')
  exitMatomo() {
    this.matomoService.trackPageView('Exit page');
  }

  ngOnInit(): void {
    if (environment.ssoEnable) {
      this.handleAuthCookie();
    }

    if (environment.trackEnable) {
      this.store
        .select(currentUserData)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((currentUser) => {
          if (!currentUser?.me) return;
          this.enableTracker();
        });
    } else {
      this.disableTracker();
    }

    this.handleReturnUrl();

    this.isProjectTablePage = this.router.url === '/spa/projects';
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.isProjectTablePage = this.router.url === '/spa/projects';
        this.matomoService.setCustomUrl(this.router.url);
        this.matomoService.trackPageView();
      }
    });
    // Track support button
    const supportButton = this.document.getElementsByClassName('zendesk-launcher')[0];
    supportButton.addEventListener('click', () => {
      this.matomoService.trackEvent('Support', 'Click', 'Question mark');
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  handleReturnUrl() {
    const pathName = this.location.path();
    // Set the return url only if the path is not empty and not includes 'authentication'
    if (pathName !== '' && !pathName.includes('authentication')) {
      this.store.dispatch(SetReturnUrl({ payload: pathName }));
    }
  }

  handleAuthCookie() {
    const match = /(?:^|;\s*)jwtToken=([^;]*)/.exec(document.cookie);
    const url = new URL(environment.siteUrl).host;
    if (match) {
      const token = match[1];
      sessionStorage.setItem(environment.authTokenName, token);
      document.cookie = `jwtToken=; Path=/; domain=${url}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure;`;
    }
    const refreshMatch = /(?:^|;\s*)refresh-token=([^;]*)/.exec(document.cookie);
    if (refreshMatch) {
      const token = refreshMatch[1];
      sessionStorage.setItem('refresh-token', token);
      document.cookie = `refresh-token=; Path=/; domain=${url}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure;`;
    }
  }

  @HostListener('wheel', ['$event'])
  setZoomByWheel(event: WheelEvent) {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const delta = Math.sign(event.deltaY);
    delta === -1 ? this.store.dispatch(ZoomIn()) : this.store.dispatch(ZoomOut());
  }

  @HostListener('document:keydown', ['$event'])
  setZoomByKey(event: KeyboardEvent) {
    if (!event.ctrlKey) return;
    if (!['0', '-', '='].includes(event.key)) return;
    event.preventDefault();
    switch (event.key) {
      case '0':
        this.store.dispatch(ZoomReset());
        break;
      case '-':
        this.store.dispatch(ZoomOut());
        break;
      case '=':
        this.store.dispatch(ZoomIn());
        break;
    }
  }
  registerMatomoEvent() {
    this.matomoService.trackEvent('Open new tab', 'Click', 'Powered by Beaconcure');
  }

  private disableTracker() {
    const window = this.document.defaultView;
    if (!this.isTrackingEnabled && this.old_paq) return;
    if (window['_paq'].splice) {
      // clean old track messages
      window['_paq'].splice(0);
    }
    this.old_paq = window['_paq'];
    window['_paq'] = {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      push() {}
    };
    this.isTrackingEnabled = false;
  }

  private enableTracker() {
    const window = this.document.defaultView;
    if (this.isTrackingEnabled) return;
    if (this.old_paq) {
      window['_paq'] = this.old_paq;
    }
    if (!this.wasInitialized) {
      this.matomoService.init(environment.trackUrl, environment.trackId);
      this.wasInitialized = true;
    }
    this.isTrackingEnabled = true;
  }
}
